import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { getTimestamp } from './dateUtils';
import { getCertificationStatus, getCertificationType } from './catalogUtils';

/**
 * @function exportUsers
 * @description Exporta una lista de usuarios a un archivo Excel (.xlsx) con formato personalizado.
 * El archivo contiene columnas con nombre, usuario, correo, tipo, estado, fecha de creación y fecha de actualización.
 * También configura el ancho de columnas y aplica estilos básicos si se usa con `xlsx-style`.
 *
 * @param {Array<Object>} data - Lista de usuarios a exportar. Cada objeto debe tener las propiedades:
 *  - name: string
 *  - username: string
 *  - email: string
 *  - type: string
 *  - status: { content: string }
 *  - created: string (fecha)
 *  - updated: string (fecha)
 *
 * @returns {void} - No retorna nada. Descarga automáticamente el archivo `.xlsx` en el navegador.
 *
 * @example
 * const users = [
 *   {
 *     name: "Juan",
 *     username: "juan123",
 *     email: "juan@example.com",
 *     type: "Admin",
 *     status: { content: "Activo" },
 *     created: "2024-01-01",
 *     updated: "2024-02-01"
 *   }
 * ];
 * exportUsers(users); // Descarga un archivo users_1712345678901.xlsx
 */
export const exportUsers = async (data) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('Usuarios');

	const headers = ["Nombre", "Usuario", "Correo", "Tipo", "Estado", "Creado", "Actualizado"];

	worksheet.addRow(headers);

	// Estilo de encabezados
	headers.forEach((_, i) => {
		const cell = worksheet.getCell(1, i + 1);
		cell.font = { bold: true };
		cell.alignment = { horizontal: 'center' };
	});

	// Agregar datos
	data.forEach((row) => {
		const rowData = [
			row.name,
			row.username,
			row.email,
			row.type,
			row.status?.content || '',
			row.created,
			row.updated
		];

		const addedRow = worksheet.addRow(rowData);

		// Centrar las columnas: Tipo, Estado
		[4, 5].forEach(colIdx => {
			addedRow.getCell(colIdx).alignment = { horizontal: 'center' };
		});
	});

	// Ajuste de ancho por columna
	worksheet.columns = [
		{ width: 30 },  // Nombre
		{ width: 20 },  // Usuario
		{ width: 28 },  // Correo
		{ width: 14 },  // Tipo
		{ width: 14 },  // Estado
		{ width: 18 },  // Creado
		{ width: 18 }   // Actualizado
	];

	// Exportar archivo
	const buffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([buffer], { type: 'application/octet-stream' });
	saveAs(blob, `users_${getTimestamp()}.xlsx`);
};

/**
 * @function exportCandidates
 * @description Exporta una lista de candidatos a un archivo Excel (.xlsx) con formato personalizado.
 *
 * El archivo incluye columnas como:
 * Nombre, Email, Colegio, Certificación, Tipo, Estatus, Fecha Inicio, Fecha Fin, Fecha Finalizado.
 *
 * @param {Array<Object>} data - Lista de candidatos a exportar. Cada objeto debe tener:
 *  - name: string
 *  - email: string
 *  - college_name: string
 *  - catalog_id: string
 *  - certification_type: string
 *  - certification_status: string
 *  - assigned_date: string
 *  - closed_date: string
 *  - completed_date: string
 *
 * @returns {void} Descarga automáticamente el archivo `.xlsx` en el navegador.
 */

/**
 * @function exportCandidates
 * @description Exporta un listado de candidatos con su información general en un archivo Excel (.xlsx).
 *
 * @param {Array<Object>} data - Lista de candidatos. Cada objeto debe tener:
 *  - name
 *  - email
 *  - college_name
 *  - catalog_id
 *  - certification_type
 *  - certification_status
 *  - assigned_date
 *  - closed_date
 *  - completed_date
 */
export const exportCandidates = async (data) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('Candidatos');

	const headers = [
		"Nombre",
		"Email",
		"Colegio",
		"Certificación",
		"Tipo",
		"Estatus",
		"Fecha Inicio",
		"Fecha Fin",
		"Fecha Finalizado"
	];

	// Agregar encabezado
	worksheet.addRow(headers);

	// Estilo del encabezado
	headers.forEach((_, idx) => {
		const cell = worksheet.getCell(1, idx + 1);
		cell.font = { bold: true };
		cell.alignment = { horizontal: 'center' };
	});

	// Agregar datos
	data.forEach(row => {
		const rowData = [
			row.name,
			row.email,
			row.college_name,
			row.catalog_id,
			row.certification_type,
			row.certification_status,
			row.assigned_date,
			row.closed_date,
			row.completed_date
		];
		worksheet.addRow(rowData);
	});

	// Ajuste de columnas
	const columnWidths = [40, 30, 26, 24, 18, 22, 22, 22, 26]; // ancho aproximado en caracteres
	worksheet.columns = columnWidths.map(width => ({ width }));

	// Exportar archivo
	const buffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([buffer], { type: 'application/octet-stream' });
	saveAs(blob, `candidates_${getTimestamp()}.xlsx`);
};


/**
 * @function exportEvaluatedEpst
 * @description Exporta una lista de resultados evaluados EPST a un archivo Excel (.xlsx).
 *
 * @param {Array<Object>} data - Lista de resultados EPST. Cada objeto debe tener:
 *  - index, 
 * 	- username,
 *  - name, 
 *  - email, 
 *  - college_name, 
 *  - certification_type, 
 *  - candidate_type,
 *  - assigned_date, 
 *  - completed_date, 
 *  - grammar_score, 
 *  - reading_score, 
 *  - listening_score,
 *  - writing_score, 
 *  - speaking_score, 
 *  - total_score, 
 *  - level, 
 *  - status
 */
export const exportEvaluatedEpst = async (data) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('EPST Evaluadas');

	const headers = [
		"#", "USERNAME", "EMAIL", "NOMBRE", "COLEGIO", "CERTIFICACION", "ETAPA", "TIPO CANDIDATO", "FECHA ASIGNADA",
		"GRAMMAR", "READING", "LISTENING", "WRITING", "SPEAKING",
		"TOTAL SCORE", "NIVEL", "FECHA COMPLETADA", "ESTATUS"
	];

	worksheet.addRow(headers);

	headers.forEach((_, i) => {
		const cell = worksheet.getCell(1, i + 1);
		cell.font = { bold: true };
		cell.alignment = { horizontal: 'center' };
	});

	const centerCols = [10, 11, 12, 13, 14, 15, 16]; // columnas con datos numéricos

	data.forEach((row, i) => {
		const rowData = [
			i + 1,
			row.username || '',  // Asegura que no truene si falta
			row.email,
			row.name,
			row.college_name,
			row.certification_type,
			'EPSt', // EPST: si tienes stage, ponlo aquí
			row.candidate_type,
			row.assigned_date,
			row.grammar_score,
			row.reading_score,
			row.listening_score,
			row.writing_score,
			row.speaking_score,
			row.total_score,
			row.level,
			row.completed_date ? row.completed_date : 'PENDIENTE',
			row.status
		];

		const addedRow = worksheet.addRow(rowData);

		centerCols.forEach(colIdx => {
			addedRow.getCell(colIdx).alignment = { horizontal: 'center' };
		})
	});

	worksheet.columns = headers.map((_, idx) => {
		if (idx === 0) return { width: 7 };  // índice
		if (centerCols.includes(idx + 1)) return { width: 18 };
		return { width: 22 };
	});

	const buffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([buffer], { type: 'application/octet-stream' });
	saveAs(blob, `epst_evaluadas_${getTimestamp()}.xlsx`);
};


/**
 * @function exportEvaluatedEmet
 * @description Exporta una lista de resultados evaluados EMeT a un archivo Excel (.xlsx).
 *
 * @param {Array<Object>} data - Lista de resultados EMeT. Cada objeto debe tener:
 *  - index
 *  - certification_id
 *  - name
 *  - email
 *  - college
 *  - assigned_date (formato: DD/MM/YYYY)
 *  - completed_date (formato: DD/MM/YYYY HH:mm:ss o 'PENDIENTE')
 *  - certification_type (string)
 *  - total_score (número)
 *  - total_percentage (string con %)
 *  - level (string)
 *  - section_1 to section_4 (número)
 *  - percentage_section_1 to percentage_section_4 (string con %)
 */
export const exportEvaluatedEmet = async (data) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('EMeT Evaluadas');

	const headers = [
		"#", "USERNAME", "EMAIL", "NOMBRE", "COLEGIO", "CERTIFICACION", "ETAPA", "FECHA ASIGNADA",
		"SECCIÓN 1", "SECCIÓN 2", "SECCIÓN 3", "SECCIÓN 4",
		"TOTAL SCORE", "NIVEL", "FECHA COMPLETADA", "STATUS"
	];

	// Agregar encabezado
	worksheet.addRow(headers);

	// Estilo de encabezados
	headers.forEach((_, i) => {
		const cell = worksheet.getCell(1, i + 1);
		cell.font = { bold: true };
		cell.alignment = { horizontal: 'center' };
	});

	// Definir columnas que deben estar centradas
	const centerCols = [9, 10, 11, 12, 13, 14]; // columnas base 1

	// Agregar datos
	data.forEach((row, i) => {
		const rowData = [
			i + 1,
			row.username,
			row.name,
			row.email,
			row.college,
			getCertificationType(row.certification_type),
			"EMeT",
			row.assigned_date
				? new Date(row.assigned_date).toLocaleString('es-MX', {
					day: '2-digit',
					month: '2-digit',
					year: '2-digit'
				})
				: 'PENDIENTE',
			row.total_section_1,
			row.total_section_2,
			row.total_section_3,
			row.total_section_4,
			row.total_score,
			row.level,
			row.completed_date
				? new Date(row.completed_date).toLocaleString('es-MX', {
					day: '2-digit',
					month: '2-digit',
					year: '2-digit',
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					hour12: false
				})
				: 'PENDIENTE',
			getCertificationStatus(row.status)
		];

		const addedRow = worksheet.addRow(rowData);

		// Centrar columnas específicas
		centerCols.forEach(colIdx => {
			addedRow.getCell(colIdx).alignment = { horizontal: 'center' };
		});
	});

	// Configurar ancho de columnas
	worksheet.columns = headers.map((header, idx) => {
		if (idx === 0) return { width: 7 }; // índice `#` (ancho ~50px)
		if (centerCols.includes(idx + 1)) return { width: 18 }; // ~100px
		return { width: 22 }; // ~130px
	});

	// Exportar archivo
	const buffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([buffer], { type: 'application/octet-stream' });
	saveAs(blob, `emet_evaluadas_${getTimestamp()}.xlsx`);
};


/**
 * @function exportEpstEstadistico
 * @description Genera un archivo Excel con los resultados estadísticos EPST. Los encabezados y las respuestas son dinámicos.
 *
 * @param {string[]} headers - Encabezados de preguntas en el formato "número - tipo - pregunta".
 * @param {Array<Object>} data - Arreglo de respuestas por usuario.
 *   Cada objeto debe tener al menos: { email, name, "1 - Kids - Choose:": 1, ... }
 */
export const exportEpstEstadistico = async (headers, data) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('EPSt Estadístico');

	// Encabezados: Email, Name + dinámicos
	const fixedHeaders = ['Nombre', 'Email'];
	const excelHeaders = [...fixedHeaders, ...headers];
	worksheet.addRow(excelHeaders);

	// Estilo de encabezado
	excelHeaders.forEach((_, idx) => {
		const cell = worksheet.getCell(1, idx + 1);
		cell.font = { bold: true };
	});

	// Cada fila en `data` es una fila del Excel
	data.forEach((row) => {
		const reorderedRow = [row[1], row[0], ...row.slice(2)];
		worksheet.addRow(reorderedRow);
	});

	// Ajustar anchos de columna
	worksheet.columns = excelHeaders.map((_, idx) => {
		if (idx < 2) return { width: 30 }; // Email, Name
		return { width: 20 };              // Preguntas
	});

	// Exportar archivo
	const buffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([buffer], { type: 'application/octet-stream' });
	saveAs(blob, `epst_estadistico_${getTimestamp()}.xlsx`);
};

/**
 * @function exportEmetEstadistico
 * @description Genera un archivo Excel con los resultados estadísticos EMeT. Los encabezados y las respuestas son dinámicos.
 *
 * @param {string[]} headers - Encabezados de preguntas en el formato "número - tipo - pregunta".
 * @param {Array<Object>} data - Arreglo de respuestas por usuario.
 *   Cada objeto debe tener al menos: { email, name, "1 - Kids - Choose:": 1, ... }
 */
export const exportEmetEstadistico = async (headers, data) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('EMeT Estadístico');

	// Encabezados: Email, Name + dinámicos
	const fixedHeaders = ['Nombre', 'Email'];
	const excelHeaders = [...fixedHeaders, ...headers];
	worksheet.addRow(excelHeaders);

	// Estilo de encabezado
	excelHeaders.forEach((_, idx) => {
		const cell = worksheet.getCell(1, idx + 1);
		cell.font = { bold: true };
	});

	// Cada fila en `data` es una fila del Excel
	data.forEach((row) => {
		const reorderedRow = [row[1], row[0], ...row.slice(2)];
		worksheet.addRow(reorderedRow);
	});

	// Ajustar anchos de columna
	worksheet.columns = excelHeaders.map((_, idx) => {
		if (idx < 2) return { width: 30 }; // Email, Name
		return { width: 20 };              // Preguntas
	});

	// Exportar archivo
	const buffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([buffer], { type: 'application/octet-stream' });
	saveAs(blob, `emet_estadistico_${getTimestamp()}.xlsx`);
};